import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Package, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl tracking-tight">GestMob</h1>
              <p className="text-sm text-slate-400">Gestion du Mobilier</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="font-heading text-4xl font-bold leading-tight">
              Gérez votre mobilier<br />
              de bureau efficacement
            </h2>
            <p className="text-slate-400 text-lg max-w-md">
              Inventaire centralisé, traçabilité complète et scan par code-barres pour une gestion optimale de vos actifs.
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-slate-400">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-400" />
            </div>
            <span>Suivi du cycle de vie complet</span>
          </div>
          <div className="flex items-center gap-4 text-slate-400">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-400" />
            </div>
            <span>Multi-sites et multi-localisations</span>
          </div>
          <div className="flex items-center gap-4 text-slate-400">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-400" />
            </div>
            <span>Rapports et exports détaillés</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">GestMob</h1>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Connexion</h2>
            <p className="text-slate-500 mt-2">Accédez à votre espace de gestion</p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 btn-scale"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-6">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-blue-600 hover:underline font-medium">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
